<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { DateInput } from "date-picker-svelte";
	import calendarIcon from "@core/assets/icons/calendar.svg?raw";
	import errorIcon from "@core/assets/icons/error.svg?raw";
	import { Locale } from "@core/schema/Locale.js";
	import "../../assets/css/date-picker.css";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./DatePicker.translations.json" with { type: "json" };
	import { dateInputLocale } from "./dateInputLocale.js";

	const translate = getTranslate(translations);
	const { tenant } = getGlobalTemplateProps();

	let {
		value = $bindable(),
		element = $bindable(),
		/* eslint-disable prefer-const */
		min,
		max,
		up,
		excludedDays = [],
		validationMessage,
		/* eslint-enable prefer-const */
	}: {
		value?: string;
		element?: HTMLDivElement;
		min?: Date;
		max?: Date;
		up?: boolean;
		excludedDays?: number[];
		validationMessage?: string;
	} = $props();

	function parseDate(string: string | undefined): Date | undefined {
		if (!string || Number.isNaN(Date.parse(string))) {
			return undefined;
		}
		return new Date(`${string}T12:00:00`);
	}

	function formatDate(date: Date | undefined): string | undefined {
		if (!date) {
			return undefined;
		}
		return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
	}

	const format = $derived(tenant.locale === Locale.en ? "MM/dd/yyyy" : "dd. MM. yyyy");
	const isDisabledDate = $derived(
		excludedDays.length > 0 ? (date: Date): boolean => excludedDays.includes(date.getDay()) : null,
	);

	let dateValue = $state(parseDate(value));
	const invalid = $derived(validationMessage !== undefined && validationMessage !== "");

	$effect(() => {
		value = formatDate(dateValue);
	});
</script>

<div class={["grid gap-1", up && "up", invalid && "date-invalid"]} bind:this={element}>
	<DateInput
		bind:value={dateValue}
		{min}
		{max}
		{isDisabledDate}
		closeOnSelection
		browseWithoutSelecting
		locale={dateInputLocale[tenant.locale]}
		{format}
		placeholder={translate("chooseDate")}
	/>
	<div
		class="pl-5.5 [grid-row:1] [grid-column:1] size-full flex items-center z-[1000] pointer-events-none"
		aria-hidden="true"
	>
		<Icon icon={calendarIcon} class="size-5" />
	</div>
	{#if invalid}
		<div
			class="pr-6 [grid-row:1] [grid-column:1] size-full flex items-center justify-end z-[1000] pointer-events-none"
			aria-hidden="true"
		>
			<Icon icon={errorIcon} class="size-5.5 text-fcrerror" />
		</div>
		<div class="flex justify-end text-sm text-fcrerror pr-6">
			<Formatted text={validationMessage ?? ""} />
		</div>
	{/if}
</div>
